import { apiGet, apiPost } from "./client";
import {
  currentSeasonSchema,
  formGuideSchema,
  horseRailSchema,
  liveOpensSchema,
  marketSchema,
  ownershipLeaderboardSchema,
  packHistorySchema,
  packPurchaseBatchSchema,
  packPurchaseSchema,
  packSeriesSchema,
  playerProfileSchema,
  racecardResponseSchema,
  raceResultsSchema,
  racesSchema,
  scheduleSchema,
  wageringLeaderboardSchema,
} from "./schemas";
import { publicSettingsSchema } from "../theme/schema";

export const api = {
  settings: () => apiGet("/public/settings", publicSettingsSchema),
  upcoming: (limit = 12) =>
    apiGet(`/races/upcoming?limit=${limit}`, racesSchema),
  results: (limit = 12, cursor?: string) => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (cursor) params.set("cursor", cursor);
    return apiGet(`/races/results?${params.toString()}`, raceResultsSchema);
  },
  schedule: (from: string, to: string, track?: string) => {
    const params = new URLSearchParams({ from, to });
    if (track) params.set("track", track);
    return apiGet(`/races/schedule?${params.toString()}`, scheduleSchema);
  },
  racecard: (raceId: string) =>
    apiGet(`/races/${raceId}/racecard`, racecardResponseSchema),
  playback: (raceId: string) =>
    apiGet<{
      raceId: string;
      status: string;
      startedAt: string;
      gatesOpenAt: string;
      scheduledAt: string;
      serverTime: string;
      raceDurationMs: number;
      racecardHash: string;
      order: number[];
      finishTimesMs: number[];
      marginsL100: number[];
      physics: Record<string, unknown>;
    }>(`/races/${raceId}/playback`),
  proof: (raceId: string) =>
    apiGet<Record<string, unknown>>(`/races/${raceId}/proof`),
  formGuide: (raceId: string) =>
    apiGet(`/races/${raceId}/form-guide`, formGuideSchema),
  markets: (raceId: string) =>
    apiGet(`/betting/markets/${raceId}`, marketSchema),
  quoteCoupon: (payload: {
    type: "single" | "combined" | "system";
    systemK?: number;
    stakeCents: number;
    legs: Array<{ raceId: string; betType: string; sel: number[] }>;
  }) =>
    apiPost<{
      type: string;
      systemK: number | null;
      stakeCents: number;
      potentialCents: number;
      lines: number;
      legs: Array<{
        raceId: string;
        betType: string;
        sel: number[];
        oddsH: number;
        bettingOpen: boolean;
      }>;
    }>("/betting/price", payload),
  placeCoupon: (payload: {
    type: "single" | "combined" | "system";
    systemK?: number;
    stakeCents: number;
    clientRef: string;
    legs: Array<{ raceId: string; betType: string; sel: number[] }>;
  }) => apiPost<Record<string, unknown>>("/betting/coupons", payload),
  myCoupons: (cursor?: string) =>
    apiGet<Record<string, unknown>>(
      `/betting/my${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`,
    ),
  myLedger: (cursor?: string) =>
    apiGet<Record<string, unknown>>(
      `/ledger/my${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`,
    ),
  currentSeason: () => apiGet("/seasons/current", currentSeasonSchema),
  inForm: (limit = 5) =>
    apiGet(`/horses/in-form?limit=${limit}`, horseRailSchema),
  popularHorses: (limit = 5) =>
    apiGet(`/horses/popular?limit=${limit}`, horseRailSchema),
  horse: (horseId: string) =>
    apiGet<Record<string, unknown>>(`/horses/${horseId}`),
  player: () => apiGet("/players/me", playerProfileSchema),
  balance: () =>
    apiGet<{
      balanceCents: number | null;
      cashBalanceCents: number | null;
      bonusBalanceCents: number | null;
      lockedBalanceCents: number | null;
      currency: string;
      displayName: string;
      playerId: number;
      asOf: string | null;
      source: string | null;
      stale: boolean;
      errorCode: string | null;
      walletFound: boolean | null;
      wallets:
        | {
            currency: string;
            amount: string;
            cashAmount: string;
            bonusAmount: string;
            lockedAmount: string;
            hasBalance: boolean;
          }[]
        | null;
    }>("/wallet/balance"),
  session: () =>
    apiGet<{
      authenticated: boolean;
      authMode: "dev" | "direct" | "oidc" | "unavailable";
      loginUrl: string | null;
      directLoginEnabled: boolean;
      registrationEnabled: boolean;
      player: {
        playerId: number;
        externalPlayerId: string;
        displayName: string;
        currency: string;
        region: string;
        capabilities: Record<string, boolean>;
      } | null;
    }>("/session"),
  logout: () => apiPost<{ authenticated: boolean }>("/session/logout", {}),
  directLogin: (payload: {
    username: string;
    password: string;
    rememberMe: boolean;
    totp?: string;
  }) =>
    apiPost<{
      authenticated: boolean;
      player?: {
        playerId: number;
        externalPlayerId: string;
        displayName: string;
        currency: string;
        region: string;
        capabilities: Record<string, boolean>;
      };
    }>("/session/login", payload),
  directMfa: (payload: {
    username: string;
    password: string;
    rememberMe: boolean;
    totp: string;
  }) =>
    apiPost<{
      authenticated: boolean;
      player?: {
        playerId: number;
        externalPlayerId: string;
        displayName: string;
        currency: string;
        region: string;
        capabilities: Record<string, boolean>;
      };
    }>("/session/mfa", payload),
  directRegister: (payload: {
    email: string;
    username: string;
    password: string;
    passwordConfirm: string;
    country: string;
    referralCode?: string;
    isLegalAge: boolean;
    isTermsAccepted: boolean;
    isPrivacyAccepted: boolean;
    isEmailAllowed: boolean;
    welcomeBonusOptIn: boolean;
  }) =>
    apiPost<{
      accountCreated: boolean;
      authenticated: boolean;
      message?: string;
      player?: {
        playerId: number;
        externalPlayerId: string;
        displayName: string;
        currency: string;
        region: string;
        capabilities: Record<string, boolean>;
      };
    }>("/session/register", payload),
  registrationOptions: () =>
    apiGet<{
      countries: Array<{ code: string; name: string }>;
      suggestedUsername: string;
      detectedCountry: string;
    }>("/session/registration-options"),
  devLogin: (name: string) =>
    apiPost<{
      playerId: number;
      displayName: string;
      created: boolean;
      currency: string;
      capabilities?: Record<string, boolean>;
    }>("/dev/login", { name }),
  sessionMint: (token: string) =>
    apiPost<{
      playerId: number;
      externalPlayerId: string;
      displayName: string;
      region: string;
      capabilities: Record<string, boolean>;
    }>("/session/mint", {}, undefined, {
      Authorization: `Bearer ${token}`,
    }),
  packSeries: () => apiGet("/packs/series", packSeriesSchema),
  purchasePack: (
    code: string,
    payload: { clientRef: string; quantity: number },
    idempotencyKey: string,
  ) =>
    apiPost(`/packs/${code}/purchase-batch`, payload, packPurchaseBatchSchema, {
      "Idempotency-Key": idempotencyKey,
    }),
  purchaseSinglePack: (code: string) =>
    apiPost(`/packs/${code}/purchase`, {}, packPurchaseSchema),
  packHistory: (cursor?: string) =>
    apiGet(
      `/packs/my${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`,
      packHistorySchema,
    ),
  packPurchase: (positionId: string) =>
    apiGet(`/packs/${positionId}`, packPurchaseSchema),
  liveOpens: (limit = 20) =>
    apiGet(`/packs/live-opens?limit=${limit}`, liveOpensSchema),
  packFairness: (code: string) =>
    apiGet<{
      series: string;
      commitmentVersion: string;
      commitHash: string;
      committedAt: string | null;
      status: string;
      available: number;
      drawn: number;
      seedRevealed: boolean;
      seed: string | null;
      explanation: string;
    }>(`/packs/series/${code}/fairness`),
  stable: (retired = false) =>
    apiGet<Record<string, unknown>>(`/stable?retired=${String(retired)}`),
  buybackQuote: (shareId: number) =>
    apiPost<Record<string, unknown>>("/buyback/quote", { shareId }),
  buybackExecute: (offerId: string) =>
    apiPost<Record<string, unknown>>("/buyback/execute", { offerId }),
  buybackExecuteBatch: (shareIds: number[], idempotencyKey: string) =>
    apiPost<{
      status: string;
      paidCents: number;
      items: Array<{ shareId: number; offerId: string; paidCents: number }>;
    }>("/buyback/execute-batch", { shareIds, idempotencyKey }),
  ownershipLeaderboard: (period: "weekly" | "season", season?: number) => {
    const params = new URLSearchParams({ period });
    if (season) params.set("season", String(season));
    return apiGet(
      `/leaderboards/ownership?${params.toString()}`,
      ownershipLeaderboardSchema,
    );
  },
  wageringLeaderboard: (period: "daily" | "weekly" | "monthly") =>
    apiGet(
      `/leaderboards/wagering?period=${period}`,
      wageringLeaderboardSchema,
    ),
  currentPrizePeriod: () =>
    apiGet<Record<string, unknown>>("/prizes/periods/current"),
  myAwards: () => apiGet<Record<string, unknown>>("/prizes/awards/my"),
  marketListings: () => apiGet<Record<string, unknown>>("/market/listings"),
  myMarket: () => apiGet<Record<string, unknown>>("/market/mine"),
  mySyndicates: () => apiGet<Record<string, unknown>>("/syndicates/mine"),
  searchPlayers: (query: string) =>
    apiGet<Record<string, unknown>>(
      `/players/search?q=${encodeURIComponent(query)}`,
    ),
  openSyndicate: (payload: {
    series: string;
    splits: Array<{ playerId: number; shareBp: number }>;
  }) => apiPost<Record<string, unknown>>("/syndicates/open", payload),
  paySyndicate: (syndicateId: string, entropy: string) =>
    apiPost<Record<string, unknown>>(`/syndicates/${syndicateId}/pay`, {
      entropy,
    }),
  cancelSyndicate: (syndicateId: string) =>
    apiPost<Record<string, unknown>>(`/syndicates/${syndicateId}/cancel`, {}),
  augmentCatalog: () => apiGet<Record<string, unknown>>("/augments/catalog"),
  assignAugments: (payload: {
    horseId: string;
    jockeyId?: string | null;
    trainerId?: string | null;
  }) => apiPost<Record<string, unknown>>("/augments/assign", payload),
  listHorse: (horseId: string, priceCents: number) =>
    apiPost<Record<string, unknown>>("/market/list", { horseId, priceCents }),
  buyListing: (listingId: string) =>
    apiPost<Record<string, unknown>>(`/market/listings/${listingId}/buy`, {}),
  cancelListing: (listingId: string) =>
    apiPost<Record<string, unknown>>(
      `/market/listings/${listingId}/cancel`,
      {},
    ),
  makeOffer: (
    listingId: string,
    payload: { cashCents: number; offeredHorseIds: string[] },
  ) =>
    apiPost<Record<string, unknown>>(
      `/market/listings/${listingId}/offers`,
      payload,
    ),
  acceptOffer: (offerId: string) =>
    apiPost<Record<string, unknown>>(`/market/offers/${offerId}/accept`, {}),
  declineOffer: (offerId: string) =>
    apiPost<Record<string, unknown>>(`/market/offers/${offerId}/decline`, {}),
  withdrawOffer: (offerId: string) =>
    apiPost<Record<string, unknown>>(`/market/offers/${offerId}/withdraw`, {}),
};

export * from "./client";
export * from "./schemas";
